import { useEffect, useMemo, useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { useToast } from '@/components/ui/use-toast'
import { useOrganizations } from '@/features/workers/hooks'
import {
  useCreateFicha,
  useEncounters,
  useWorkersSimple,
  type CreateFichaPayload,
} from '@/features/fichas/hooks'
import { TEMPLATE_OPTIONS, computeBmi, fieldsForTemplate } from '@/features/fichas/templates'
import type { FichaTemplate } from '@/features/fichas/types'
import { ApiError } from '@/lib/api'

export default function FichaNuevaPage() {
  const navigate = useNavigate()
  const { show } = useToast()
  const [searchParams] = useSearchParams()

  const [template, setTemplate] = useState<FichaTemplate | ''>('')
  const [organizationId, setOrganizationId] = useState<string>('')
  const [workerId, setWorkerId] = useState<string>('')
  const [encounterId, setEncounterId] = useState<string>('')
  const [values, setValues] = useState<Record<string, string>>({})
  const [customFields, setCustomFields] = useState<Array<{ key: string; value: string }>>([])
  const [error, setError] = useState<string | null>(null)

  const { data: orgsData } = useOrganizations()
  const { data: workersData } = useWorkersSimple({
    organizationId: organizationId || undefined,
  })
  const { data: encountersData, isLoading: encountersLoading } = useEncounters({
    organizationId: organizationId || undefined,
  })

  const createMutation = useCreateFicha()

  // #20: deep-link del expediente — pre-seleccionar empresa + trabajador desde ?org= / ?worker=.
  // El Select de trabajador está deshabilitado sin empresa, por eso se setea la empresa también.
  useEffect(() => {
    const orgParam = searchParams.get('org')
    const workerParam = searchParams.get('worker')
    if (orgParam) setOrganizationId(orgParam)
    if (workerParam) setWorkerId(workerParam)
  }, [searchParams])

  const fields = template ? fieldsForTemplate(template) : []

  // Auto-BMI cuando peso/altura cambian (solo antropometría)
  useEffect(() => {
    if (template !== 'antropometria') return
    const peso = Number(values.peso_kg)
    const altura = Number(values.altura_cm)
    if (peso && altura) {
      const bmi = computeBmi(peso, altura)
      if (bmi !== undefined) {
        setValues((prev) => ({ ...prev, bmi: String(bmi) }))
      }
    }
  }, [template, values.peso_kg, values.altura_cm])

  const orgOptions = useMemo(
    () => (orgsData?.items ?? []).map((o) => ({ value: o.id, label: o.name })),
    [orgsData],
  )
  const workerOptions = useMemo(
    () =>
      (workersData?.items ?? []).map((w) => ({
        value: w.id,
        label: `${w.familyName}, ${w.givenName} — ${w.rut}`,
      })),
    [workersData],
  )
  const encounterOptions = useMemo(
    () =>
      (encountersData?.items ?? []).map((e) => ({
        value: e.id,
        label: `${e.operativoId}${e.date ? ` — ${e.date}` : ''}`,
      })),
    [encountersData],
  )

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError(null)
    if (!template || !workerId || !encounterId) {
      setError('Completa template, trabajador y operativo.')
      return
    }

    const parsedValues: Record<string, unknown> = {}
    for (const field of fields) {
      const raw = values[field.key]
      if (!raw) continue
      parsedValues[field.key] =
        field.type === 'number' ? Number(raw) : field.type === 'yesno' ? raw === 'si' : raw
    }

    const customObj: Record<string, unknown> = {}
    for (const row of customFields) {
      const k = row.key.trim()
      if (!k) continue
      const v = row.value.trim()
      customObj[k] = isNaN(Number(v)) ? v : Number(v)
    }

    const payload: CreateFichaPayload = {
      worker_id: workerId,
      encounter_id: encounterId,
      template,
      values: parsedValues,
      ...(Object.keys(customObj).length > 0 ? { custom_fields: customObj } : {}),
    }

    try {
      const created = await createMutation.mutateAsync(payload)
      show({
        kind: 'success',
        title: 'Ficha creada',
        description: 'La evaluación quedó registrada correctamente.',
      })
      navigate(`/app/fichas/${encodeURIComponent(created.id)}`)
    } catch (err) {
      if (err instanceof ApiError) {
        setError(
          err.body?.error === 'forbidden'
            ? 'No tienes permisos para crear fichas.'
            : 'No se pudo guardar la ficha.',
        )
      } else {
        setError('Error de conexión.')
      }
    }
  }

  return (
    <div className="flex flex-col gap-5" data-testid="ficha-nueva-page">
      <div>
        <Link
          to="/app/fichas"
          className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Volver a fichas
        </Link>
        <h1 className="mt-2 text-2xl font-bold tracking-tight text-slate-900">
          Nueva ficha clínica
        </h1>
        <p className="text-sm text-slate-600">
          Selecciona el template, trabajador, operativo y completa los campos.
        </p>
      </div>

      <form onSubmit={onSubmit} className="flex flex-col gap-5" noValidate>
        <Card>
          <CardHeader>
            <CardTitle>Template</CardTitle>
            <CardDescription>Elige el tipo de ficha a registrar.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="max-w-md">
              <Select
                value={template}
                onChange={(v) => {
                  setTemplate(v as FichaTemplate)
                  setValues({})
                }}
                options={TEMPLATE_OPTIONS.map((o) => ({
                  value: o.value,
                  label: o.label,
                  description: o.description,
                }))}
                placeholder="Seleccionar template"
                data-testid="select-ficha-template"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Contexto</CardTitle>
            <CardDescription>Empresa, trabajador y operativo de la evaluación.</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Select
              label="Empresa contratante"
              value={organizationId}
              onChange={(v) => {
                setOrganizationId(v)
                setWorkerId('')
                setEncounterId('')
              }}
              options={orgOptions}
              placeholder="Elegir empresa"
              data-testid="ficha-select-org"
            />
            <Select
              label="Trabajador"
              value={workerId}
              onChange={setWorkerId}
              options={workerOptions}
              placeholder="Elegir trabajador"
              disabled={!organizationId}
              data-testid="ficha-select-worker"
            />
            <Select
              label="Operativo"
              value={encounterId}
              onChange={setEncounterId}
              options={encounterOptions}
              placeholder="Elegir operativo"
              disabled={!organizationId}
              isLoading={encountersLoading}
              emptyMessage="Sin operativos para esta empresa. Crea uno en Admin → Operativos."
              data-testid="ficha-select-encounter"
            />
          </CardContent>
        </Card>

        {template && (
          <Card>
            <CardHeader>
              <CardTitle>Mediciones</CardTitle>
              <CardDescription>Ingresa los valores. Los campos vacíos se omiten.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {fields.map((field) => {
                  if (field.type === 'yesno') {
                    return (
                      <Select
                        key={field.key}
                        label={field.label}
                        value={values[field.key] ?? ''}
                        onChange={(v) => setValues((prev) => ({ ...prev, [field.key]: v }))}
                        options={[
                          { value: '', label: 'Sin responder' },
                          { value: 'si', label: 'Sí' },
                          { value: 'no', label: 'No' },
                        ]}
                        data-testid={`field-${field.key}`}
                      />
                    )
                  }
                  return (
                    <Input
                      key={field.key}
                      label={field.label}
                      type={field.type}
                      value={values[field.key] ?? ''}
                      onChange={(e) =>
                        setValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                      }
                      placeholder={field.placeholder}
                      min={field.min}
                      max={field.max}
                      step="any"
                      rightAdornment={
                        field.unit && <span className="text-xs text-slate-500">{field.unit}</span>
                      }
                      data-testid={`field-${field.key}`}
                    />
                  )
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {template && (
          <Card>
            <CardHeader>
              <CardTitle>Campos personalizados</CardTitle>
              <CardDescription>
                Agrega mediciones específicas fuera del template estándar.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {customFields.length > 0 && (
                <div className="mb-3 flex flex-col gap-2">
                  {customFields.map((row, idx) => (
                    <div key={idx} className="flex gap-2">
                      <Input
                        placeholder="Nombre del campo"
                        value={row.key}
                        onChange={(e) =>
                          setCustomFields((prev) =>
                            prev.map((r, i) => (i === idx ? { ...r, key: e.target.value } : r)),
                          )
                        }
                        data-testid={`custom-key-${idx}`}
                      />
                      <Input
                        placeholder="Valor"
                        value={row.value}
                        onChange={(e) =>
                          setCustomFields((prev) =>
                            prev.map((r, i) => (i === idx ? { ...r, value: e.target.value } : r)),
                          )
                        }
                        data-testid={`custom-value-${idx}`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label="Eliminar campo"
                        onClick={() => setCustomFields((prev) => prev.filter((_, i) => i !== idx))}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                leftIcon={<Plus className="h-3.5 w-3.5" />}
                onClick={() => setCustomFields((prev) => [...prev, { key: '', value: '' }])}
                data-testid="add-custom-field"
              >
                Agregar campo custom
              </Button>
            </CardContent>
          </Card>
        )}

        {error && (
          <div
            role="alert"
            className="rounded-md border border-[color:var(--color-fitlab-red)]/30 bg-[color:var(--color-fitlab-red)]/5 px-3 py-2 text-sm text-[color:var(--color-fitlab-red-hover)]"
          >
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Link to="/app/fichas">
            <Button type="button" variant="ghost">
              Cancelar
            </Button>
          </Link>
          <Button type="submit" loading={createMutation.isPending} data-testid="ficha-submit">
            Guardar ficha
          </Button>
        </div>
      </form>
    </div>
  )
}
